import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, AlertCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const ErrosConstrutoras = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre erros em entrega de obras e gostaria de evitá-los.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/erros-construtoras'].title}
        description={seoData['/blog/erros-construtoras'].description}
        canonical={seoData['/blog/erros-construtoras'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Início</button>
              <button
                onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Serviços
              </button>
              <button
                onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400">
                Limpeza Pós-Obra
              </button>
              <button
                onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Locação
              </button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded text-green-400 font-semibold">Blog</button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button onClick={() => navigate('/blog')} className="flex items-center text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              7 Erros Que Construtoras Cometem ao Entregar a Obra
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /><span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" /><span>9 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Construtoras e incorporadoras frequentemente cometem <strong>erros evitáveis</strong> na entrega de obras que comprometem a satisfação do cliente e geram custos extras. Conheça os 7 erros mais comuns.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erro #1: Entregar Obra Sem Limpeza Profissional
            </h2>

            <Card className="bg-red-50 border-2 border-red-200 mb-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">O Problema:</h3>
                    <p className="text-gray-700 mb-3">
                      Entregar imóvel com resíduos de cimento, pó, manchas e sujeira de obra causa péssima primeira impressão.
                    </p>
                    <h4 className="font-semibold text-gray-900 mb-2">Consequências:</h4>
                    <ul className="text-gray-700 space-y-1">
                      <li>• Cliente insatisfeito desde o início</li>
                      <li>• Reclamações e avaliações negativas</li>
                      <li>• Possíveis retenções de pagamento</li>
                      <li>• Danos à reputação da construtora</li>
                    </ul>
                    <h4 className="font-semibold text-green-700 mt-3">✓ Solução:</h4>
                    <p className="text-gray-700">Contratar limpeza pós-obra profissional ANTES da vistoria de entrega.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erro #2: Usar Limpeza Convencional ao Invés de Especializada
            </h2>

            <p className="text-gray-700 mb-4">
              <strong>O Problema:</strong> Contratar diaristas ou equipes de limpeza comum que não têm equipamentos nem produtos adequados.
            </p>
            <p className="text-gray-700 mb-6">
              <strong>Resultado:</strong> Manchas de cimento não removidas, vidros riscados, pisos com aspecto ruim.
            </p>
            <p className="text-green-700 font-semibold mb-6">
              ✓ Solução: Contratar empresa especializada em limpeza pós-obra com equipamentos industriais.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erro #3: Não Programar Tempo Suficiente para Limpeza
            </h2>

            <p className="text-gray-700 mb-4">
              <strong>O Problema:</strong> Deixar apenas 1 dia para limpar um imóvel de 100m² ou mais.
            </p>
            <p className="text-gray-700 mb-6">
              <strong>Resultado:</strong> Limpeza apressada, mal feita, com detalhes negligenciados.
            </p>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">Tempo Adequado por Imóvel:</h4>
              <ul className="text-gray-700 space-y-2">
                <li>• Apartamento 2 quartos: 1-2 dias</li>
                <li>• Apartamento 3 quartos: 1-2 dias</li>
                <li>• Casa 3 quartos: 2-3 dias</li>
                <li>• Sobrado ou casa grande: 3-4 dias</li>
              </ul>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erro #4: Fazer Limpeza Antes de Finalizar Pequenos Reparos
            </h2>

            <Card className="bg-yellow-50 border-2 border-yellow-200 mb-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                  <div>
                    <p className="text-gray-700 mb-3">
                      Fazer a limpeza e depois voltar para pequenos reparos (retoques de pintura, ajustes) suja tudo novamente.
                    </p>
                    <p className="text-green-700 font-semibold">
                      ✓ Solução: Finalize 100% da obra, incluindo retoques, ANTES de contratar a limpeza.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erro #5: Não Proteger Áreas Já Limpas
            </h2>

            <p className="text-gray-700 mb-4">
              <strong>O Problema:</strong> Limpar o piso mas continuar transitando com botas sujas da obra.
            </p>
            <p className="text-gray-700 mb-6">
              <strong>Resultado:</strong> Retrabalho e desperdício de tempo e dinheiro.
            </p>
            <p className="text-green-700 font-semibold mb-6">
              ✓ Solução: Após limpeza, proibir acesso com sapatos/botas da obra. Usar proteções se necessário.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erro #6: Economizar na Limpeza e Gastar com Reclamações
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-red-700 mb-3">Cenário Real:</h4>
              <p className="text-gray-700 mb-4">
                Construtora economiza R$ 1.000 na limpeza profissional → Cliente insatisfeito reclama → Construtora precisa:
              </p>
              <ul className="text-gray-700 space-y-2">
                <li>• Contratar limpeza de emergência: R$ 1.500 (mais cara)</li>
                <li>• Atrasar entrega de chaves: Multa contratual</li>
                <li>• Lidar com reclamação formal: Tempo da equipe</li>
                <li>• Avaliação negativa online: Perda de vendas futuras</li>
              </ul>
              <p className="text-green-700 font-semibold mt-4">
                ✓ Investir em limpeza profissional desde o início previne todos esses custos!
              </p>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erro #7: Não Fazer Vistoria Pré-Entrega
            </h2>

            <p className="text-gray-700 mb-4">
              <strong>O Problema:</strong> Entregar as chaves sem uma vistoria final detalhada.
            </p>
            <p className="text-gray-700 mb-6">
              <strong>Resultado:</strong> Cliente encontra problemas que poderiam ter sido corrigidos antes.
            </p>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">✓ Checklist Pré-Entrega:</h3>
                <ol className="list-decimal list-inside text-gray-700 space-y-2">
                  <li>Todos os acabamentos finalizados?</li>
                  <li>Limpeza profissional completa?</li>
                  <li>Vidros e metais polidos?</li>
                  <li>Ralos e torneiras funcionando?</li>
                  <li>Iluminação toda testada?</li>
                  <li>Portas e janelas abrindo perfeitamente?</li>
                  <li>Manual do proprietário preparado?</li>
                </ol>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Bônus: Dicas para Construtoras e Incorporadoras
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Inclua limpeza profissional no orçamento desde o início</li>
              <li>Tenha parceria com empresa de limpeza pós-obra confiável</li>
              <li>Programe limpeza com 1 semana de antecedência da entrega</li>
              <li>Tire fotos "antes e depois" para marketing</li>
              <li>Use a limpeza impecável como diferencial de vendas</li>
            </ul>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Construtora ou Incorporadora?</h3>
                <p className="mb-6">
                  A WellTec oferece serviços especializados para entrega de obras em BH com qualidade garantida
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Parceria para Entregas
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <button onClick={handleWhatsAppClick} className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50">
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default ErrosConstrutoras;
